import type { PlaybookReadiness } from '@doneverse/shared'
import { WretchError } from 'wretch/resolver'

export function getErrorStatus(error: unknown): number | null {
  if (error instanceof WretchError) {
    return error.status ?? error.response?.status ?? null
  }

  if (error && typeof error === 'object') {
    const maybeError = error as { status?: unknown; response?: { status?: unknown } }
    if (typeof maybeError.status === 'number') return maybeError.status
    if (typeof maybeError.response?.status === 'number') return maybeError.response.status
  }

  return null
}

export function isAuthErrorStatus(status: number | null): boolean {
  return status === 401 || status === 403
}

export function isAuthHttpError(error: unknown): boolean {
  return isAuthErrorStatus(getErrorStatus(error))
}

export function isNotFoundError(error: unknown): boolean {
  return getErrorStatus(error) === 404
}

/**
 * 409 — the server's state moved on under us: the resource already exists, or
 * has already left the state the write required. Any cached copy is stale by
 * definition, so callers should refetch (or retry as an update) rather than just
 * surface the message.
 */
export function isConflictError(error: unknown): boolean {
  return getErrorStatus(error) === 409
}

/**
 * 403 — the caller doesn't hold the admin/escalation_officer role at all
 * (`requireRole('admin', 'escalation_officer')` failed). An officer whose
 * role qualifies but whose pod-scope doesn't cover this doer gets a 404
 * instead — see `isNotFoundError` — so an out-of-scope doer isn't confirmed
 * to exist.
 */
export function isForbiddenError(error: unknown): boolean {
  return getErrorStatus(error) === 403
}

/**
 * 400 — the server rejected the request's own parameters (for example an
 * out-of-range date window). Retrying the same request cannot succeed.
 */
export function isBadRequestError(error: unknown): boolean {
  return getErrorStatus(error) === 400
}

export function isNoDoerAssignedError(error: unknown): boolean {
  if (getErrorStatus(error) !== 403) return false

  if (error instanceof WretchError) {
    return error.message.toLowerCase().includes('no doer assigned')
  }

  if (error && typeof error === 'object' && 'message' in error) {
    const { message } = error as { message?: unknown }
    return typeof message === 'string' && message.toLowerCase().includes('no doer assigned')
  }

  return false
}

/**
 * The parsed body of a failed request, or null.
 *
 * `.json` is attached by `normalizeApiError` (api.ts) — wretch itself sets
 * neither `.json` nor `.text`, only `response`/`status`/`message`. `.text` is
 * still read because an error may arrive from somewhere that does populate it.
 *
 * Needed wherever an error body carries more than a message — the CMS publish
 * guards answer a 422 with the list of what is still missing, and a bare
 * "Request failed" would throw that away.
 */
export function readErrorBody(error: unknown): Record<string, unknown> | null {
  const parsed = (error as { json?: unknown })?.json
  if (parsed && typeof parsed === 'object') return parsed as Record<string, unknown>

  const text = (error as { text?: unknown })?.text
  if (typeof text !== 'string') return null
  try {
    return JSON.parse(text) as Record<string, unknown>
  } catch {
    return null
  }
}

/**
 * A refused publish (422) as the readiness it names, or null when the error is
 * something else.
 *
 * Every CMS publish guard answers the same way — `{ error, blocking, warnings }`
 * — because both surfaces render the result through the same
 * `ReadinessChecklist`. The list is the actionable part; a bare "Request failed"
 * would throw away the one thing the author needs. Each service wraps this in
 * its own error class (`PlaybookNotReadyError`, `CourseNotReadyError`) so a hook
 * can tell a refusal from a real failure.
 */
export function readNotReadyPayload(error: unknown): Pick<PlaybookReadiness, 'blocking' | 'warnings'> | null {
  if (getErrorStatus(error) !== 422) return null

  const body = readErrorBody(error)
  if (!body || !Array.isArray(body.blocking)) return null

  return {
    blocking: body.blocking as PlaybookReadiness['blocking'],
    warnings: Array.isArray(body.warnings) ? (body.warnings as PlaybookReadiness['warnings']) : [],
  }
}
